import {
  Badge,
  DwellingType,
  KitchenCountertop,
  ListingHistoryState,
  ListingState,
  ListingTag,
  MasterBedroomLocation,
} from '__generated__/athena/graphql';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { ClientError } from 'graphql-request';

import type {
  GetMarketplacePropertyDetailsQuery,
  MarketplaceBuyDirectStatus,
  MarketplaceDisplayBadge,
  MarketplaceDwellingType,
  MarketplaceKitchenCountertop,
  MarketplaceListingHistoryState,
  MarketplaceListingState,
  MarketplacePrimaryBedroomLocation,
} from './__generated__/marketplace-property.zeus';

const displayBadge = {
  AVAILABLE_SOON: Badge.AVAILABLE_SOON,
  IN_CONTRACT: Badge.IN_CONTRACT,
  ON_THE_MARKET: Badge.ON_THE_MARKET,
  RECENTLY_SOLD: Badge.RECENTLY_SOLD,
  TEMPORARILY_UNAVAILABLE: Badge.TEMP_UNAVAILABLE,
  TRADITIONAL: Badge.TRADITIONAL,
  UNKNOWN: Badge.UNKNOWN,
} satisfies Record<MarketplaceDisplayBadge, Badge>;

const dwellingType = {
  CONDO: DwellingType.APARTMENT,
  HOUSE: DwellingType.SINGLE_FAMILY,
  MULTI_FAMILY: DwellingType.MULTI_FAMILY,
  OTHER: DwellingType.OTHER,
  TOWNHOUSE: DwellingType.TOWNHOUSE,
} satisfies Record<MarketplaceDwellingType, DwellingType>;

const listingTags = {
  AVAILABLE: [ListingTag.BUY_DIRECT_ELIGIBLE],
  BACKUP_OFFER: [ListingTag.BUY_DIRECT_ELIGIBLE, ListingTag.BUY_DIRECT_VERBAL_AGREEMENT],
  IN_CONTRACT: [ListingTag.BUY_DIRECT_ELIGIBLE, ListingTag.BUY_DIRECT_SALE_PENDING],
  OFFER_DEADLINE: [ListingTag.BUY_DIRECT_ELIGIBLE, ListingTag.BUY_DIRECT_MULTIPLE_OFFERS],
} satisfies Record<MarketplaceBuyDirectStatus, ListingTag[]>;

const kitchenCountertop = {
  CORIAN: KitchenCountertop.CORIAN,
  FORMICA: KitchenCountertop.FORMICA,
  GRANITE_SLAB: KitchenCountertop.GRANITE_SLAB,
  GRANITE_TILE: KitchenCountertop.GRANITE_TILE,
  OTHER_TILE: KitchenCountertop.OTHER_TILE,
  QUARTZ: KitchenCountertop.QUARTZ,
  UNKNOWN: KitchenCountertop.UNKNOWN,
} satisfies Record<MarketplaceKitchenCountertop, KitchenCountertop>;

const listingHistoryState = {
  COMING_SOON: ListingHistoryState.COMING_SOON,
  FOR_SALE: ListingHistoryState.FOR_SALE,
  OFF_MARKET: ListingHistoryState.OFF_MARKET,
  SALE_PENDING: ListingHistoryState.SALE_PENDING,
  SOLD: ListingHistoryState.SOLD,
  UNKNOWN: ListingHistoryState.UNKNOWN,
  WITHDRAWN: ListingHistoryState.WITHDRAWN,
} satisfies Record<MarketplaceListingHistoryState, ListingHistoryState>;

const listingState = {
  AVAILABLE_SOON: ListingState.AVAILABLE_SOON,
  IN_CONTRACT: ListingState.IN_CONTRACT,
  ON_THE_MARKET: ListingState.ON_THE_MARKET,
  RECENTLY_SOLD: ListingState.RECENTLY_SOLD,
  UNKNOWN: ListingState.UNKNOWN,
} satisfies Record<MarketplaceListingState, ListingState>;

const primaryBedroomLocation = {
  BASEMENT: MasterBedroomLocation.BASEMENT,
  FIRST_FLOOR: MasterBedroomLocation.FIRST_FLOOR,
  SECOND_FLOOR: MasterBedroomLocation.SECOND_FLOOR,
  THIRD_FLOOR: MasterBedroomLocation.THIRD_FLOOR,
  UNKNOWN: MasterBedroomLocation.UNKNOWN,
} satisfies Record<MarketplacePrimaryBedroomLocation, MasterBedroomLocation>;

const dollars = (cents: number | null) => (cents == null ? null : cents / 100);

const isMarketplacePropertyDetailsQuery = (
  data: unknown,
): data is GetMarketplacePropertyDetailsQuery => {
  if (typeof data !== 'object' || data === null || !('marketplace' in data)) return false;
  const marketplace = data.marketplace;
  return typeof marketplace === 'object' && marketplace !== null && 'listingDetails' in marketplace;
};

export const getPartialMarketplacePropertyDetails = (error: unknown) => {
  const data = error instanceof ClientError ? error.response.data : undefined;
  return isMarketplacePropertyDetailsQuery(data) ? data : null;
};

export const mapMarketplaceProperty = (data: GetMarketplacePropertyDetailsQuery) => {
  const details = data.marketplace.listingDetails;
  const listing = details?.summary;
  const availability = details?.availability;
  if (!details || !listing || !listing.slug) {
    return null;
  }

  const summaryListingState = listingState[listing.listingState] ?? ListingState.UNKNOWN;
  const currentListingState = availability
    ? (listingState[availability.state] ?? ListingState.UNKNOWN)
    : null;
  const hasKnownCurrentAvailability =
    currentListingState != null && currentListingState !== ListingState.UNKNOWN;
  if (
    (!hasKnownCurrentAvailability && summaryListingState === ListingState.UNKNOWN) ||
    (currentListingState === ListingState.UNKNOWN && listing.displayBadge !== 'TRADITIONAL')
  ) {
    return null;
  }
  const resolvedListingState = hasKnownCurrentAvailability
    ? currentListingState
    : summaryListingState;

  return {
    __typename: 'MarketplaceProperty',
    addressToken: listing.addressId,
    bathrooms: listing.bathrooms,
    bedrooms: listing.bedrooms,
    buyDirectPrice: dollars(details.buyDirect?.priceCents ?? null),
    buyDirectSavings: dollars(details.buyDirect?.savingsCents ?? null),
    canSelfTour:
      hasKnownCurrentAvailability && availability ? availability.canSelfTour : listing.canSelfTour,
    city: listing.city,
    cumulativeDaysOnMarket: null,
    daysOnMarket: null,
    description: details.description,
    displayBadge:
      displayBadge[
        hasKnownCurrentAvailability && availability
          ? availability.displayBadge
          : listing.displayBadge
      ] ?? null,
    dwellingType: dwellingType[listing.dwellingType] ?? DwellingType.OTHER,
    estimatedBuyDirectMonthlyPayment: null,
    estimatedMonthlyPayment: dollars(listing.estimatedMonthlyPaymentCents),
    exteriorStories: details.exteriorStories,
    fhaEligible: null,
    fhaEligibleDate: null,
    floorPlanPhotos: details.floorPlanPhotos.map((photo) => ({
      __typename: 'MarketplaceFloorPlanPhoto',
      label: photo.label,
      url: photo.url,
    })),
    garageSpaces: details.garageSpaces,
    hasCommunityPool: null,
    hasCurrentAvailability: availability != null,
    hasPrivatePool: null,
    hoas: null,
    inGatedCommunity: null,
    kitchenCountertop:
      details.kitchenCountertop == null
        ? null
        : (kitchenCountertop[details.kitchenCountertop] ?? KitchenCountertop.UNKNOWN),
    latitude: listing.latitude,
    listPrice: dollars(listing.listPriceCents),
    listingHistory: (details.listingHistory ?? []).map((event) => ({
      __typename: 'MarketplaceListingHistory',
      currentListPriceCents: event.currentListPriceCents,
      mls: event.mls,
      originalListPriceCents: event.originalListPriceCents,
      soldPriceCents: event.soldPriceCents,
      state: listingHistoryState[event.state] ?? ListingHistoryState.UNKNOWN,
      timestamp: event.timestamp,
    })),
    listingMetadata: null,
    listingState: resolvedListingState,
    listingTags: details.buyDirect ? (listingTags[details.buyDirect.status] ?? []) : [],
    longitude: listing.longitude,
    market: null,
    masterBedroomLocation:
      details.primaryBedroomLocation == null
        ? null
        : (primaryBedroomLocation[details.primaryBedroomLocation] ?? MasterBedroomLocation.UNKNOWN),
    photos: details.photos.map((photo) => photo.mediumUrl),
    photosLg: details.photos.map((photo) => photo.largeUrl),
    photosXl: details.photos.map((photo) => photo.extraLargeUrl),
    pricePerSqFt: dollars(listing.pricePerSquareFootCents),
    schoolDistricts: details.schoolDistricts ?? [],
    schools: (details.schools ?? []).map((school) => ({
      __typename: 'MarketplaceSchool',
      id: school.id,
      name: school.name,
      rating: school.rating,
    })),
    shouldShowGuarantee: details.shouldShowGuarantee,
    similarProperties: {
      __typename: 'MarketplacePropertyListQueryResult',
      count: details.similarListings?.length ?? 0,
      nextPage: null,
      results: (details.similarListings ?? []).map((similar) => ({
        __typename: 'MarketplacePropertyListResult',
        addressToken: similar.addressId,
        baths: similar.bathrooms,
        beds: similar.bedrooms,
        city: similar.city,
        dwellingType: dwellingType[similar.dwellingType] ?? DwellingType.OTHER,
        estimatedMonthlyPayment: dollars(similar.estimatedMonthlyPaymentCents),
        fullAddress: similar.fullAddress,
        id: similar.addressId,
        listPrice: dollars(similar.listPriceCents),
        livingSquareFootage: similar.totalLivingSquareFootage,
        neighborhood: similar.neighborhood,
        photos: similar.photoUrls,
        slug: similar.slug,
      })),
    },
    slug: listing.slug,
    sqFtLot: listing.lotSquareFootage,
    sqFtTotalLiving: listing.totalLivingSquareFootage,
    state: listing.state,
    street: listing.street,
    subdivision: null,
    unitNumber: details.unitNumber,
    unitType: details.unitType,
    yearBuilt: listing.yearBuilt == null ? null : String(listing.yearBuilt),
    zip: listing.zip,
  } satisfies MarketplaceProperty;
};
